/**
 * Copyright © 2026 Adnuntius AS.
 */
import {MoneyHelper, MoneyValue} from '../../components/util/money';

export interface Fee {
  id?: string;
  name?: string;
  type?: string;
  monetary?: MoneyValue;
  multiCurrencyMonetaryAmounts?: MoneyValue[];
  percentage?: number;
}

export interface CalculatedMoneyValue extends MoneyValue {
  maxAmount?: number;
}

function getTotalMonetaryFees(cpmFees: readonly Fee[], referenceCpm: unknown): number {
  const currency = MoneyHelper.isMoney(referenceCpm) ? referenceCpm.currency : undefined;
  return cpmFees.reduce((sum, fee) => {
    if (fee.type === 'MONETARY' && MoneyHelper.isMoney(fee.monetary) && fee.monetary.currency === currency) {
      return sum + fee.monetary.amount;
    }
    if (fee.type === 'MULTI_CURRENCY_MONETARY') {
      const matchingFee = fee.multiCurrencyMonetaryAmounts?.find(amount => amount.currency === currency);
      if (MoneyHelper.isMoney(matchingFee)) {
        return sum + matchingFee.amount;
      }
    }
    return sum;
  }, 0);
}

function getTotalPercentageFees(cpmFees: readonly Fee[]): number {
  return cpmFees.reduce((sum, fee) => {
    if (fee.type === 'PERCENTAGE' && Number.isFinite(fee.percentage)) {
      return sum + (fee.percentage || 0);
    }
    return sum;
  }, 0);
}

export function calculateNetCpm(cpmFees: readonly Fee[], grossCpm: unknown): Partial<CalculatedMoneyValue> {
  const normalizedGrossCpm = MoneyHelper.fixMoney(grossCpm);
  if (!MoneyHelper.isMoney(normalizedGrossCpm)) {
    return {};
  }

  const totalMonetaryFees = getTotalMonetaryFees(cpmFees, normalizedGrossCpm);
  const totalPercentage = getTotalPercentageFees(cpmFees);
  const maxFee = cpmFees.find(fee => MoneyHelper.isMoney(fee.monetary) && Number.isFinite(fee.monetary.maxAmount));
  const percentageFee = normalizedGrossCpm.amount * totalPercentage / 100;
  const netCpm: CalculatedMoneyValue = {
    currency: normalizedGrossCpm.currency,
    amount: normalizedGrossCpm.amount - totalMonetaryFees - percentageFee
  };

  if (maxFee?.monetary?.maxAmount !== undefined) {
    netCpm.maxAmount = netCpm.amount;
    netCpm.amount = normalizedGrossCpm.amount - totalMonetaryFees + maxFee.monetary.amount -
      maxFee.monetary.maxAmount - percentageFee;
  }
  return netCpm;
}

export function deriveGrossTargetCpm(cpmFees: readonly Fee[], netCpmTarget: unknown): Partial<MoneyValue> {
  const normalizedNetCpmTarget = MoneyHelper.fixMoney(netCpmTarget);
  if (!MoneyHelper.isMoney(normalizedNetCpmTarget)) {
    return {};
  }

  const totalMonetaryFees = getTotalMonetaryFees(cpmFees, normalizedNetCpmTarget);
  const totalPercentage = getTotalPercentageFees(cpmFees);
  if (totalPercentage >= 100) {
    return {};
  }
  return {
    currency: normalizedNetCpmTarget.currency,
    amount: 100 * (normalizedNetCpmTarget.amount + totalMonetaryFees) / (100 - totalPercentage)
  };
}
